import React, { useState, useEffect, useMemo } from "react";
import Api from "../../services/Api";

const PAGE_SIZE = 5;

// 👉 Modal de aprovação do memorial dos falecidos
export default function MemorialModal({ isOpen, onClose }) {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchMensagens = async () => {
      try {
        const response = await Api.getMensagens();

        const filtered = response.filter((item) => item.status === "-");

        const mensagensComFalecido = await Promise.all(
          filtered.map(async (item) => {
            try {
              const falecido = await Api.getFalecidosPorId(item.falecido_id);

              return {
                ...item,
                nomeFalecido: falecido.nome_completo,
              };
            } catch (error) {
              console.error(
                `Erro ao buscar falecido ${item.falecido_id}:`,
                error,
              );

              return {
                ...item,
                nomeFalecido: "",
              };
            }
          }),
        );

        setData(mensagensComFalecido);
      } catch (error) {
        console.error("Erro ao buscar mensagens:", error);
      }
    };

    if (isOpen) {
      fetchMensagens();
    }
  }, [isOpen]);

  const totalPages = Math.ceil((data?.length || 0) / PAGE_SIZE);

  const currentData = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return data.slice(start, start + PAGE_SIZE);
  }, [data, currentPage]);

  if (!isOpen) return null;

  const updateStatus = (index, status) => {
    const globalIndex = (currentPage - 1) * PAGE_SIZE + index;

    setData((prev) => {
      const updated = [...prev];

      const currentStatus = updated[globalIndex].status;

      updated[globalIndex] = {
        ...updated[globalIndex],

        // se clicar no mesmo botão -> desmarca
        status: currentStatus === status ? null : status,
      };

      return updated;
    });
  };

  const handleFinalizar = async () => {
    try {
      // aprovados
      const aprovados = data.filter((item) => item.status === "aprovado");

      // reprovados
      const reprovados = data.filter((item) => item.status === "reprovado");

      // executa tudo em paralelo
      await Promise.all([
        ...aprovados.map((item) => Api.approveMessage(item.id)),

        ...reprovados.map((item) => Api.reproveMessage(item.id)),
      ]);

      // Remove da tabela as mensagens que foram processadas
      setData((prev) =>
        prev.filter(
          (item) => item.status !== "aprovado" && item.status !== "reprovado",
        ),
      );

      // Ajusta a página caso necessário
      setCurrentPage(1);

      onClose();
    } catch (error) {
      console.error("Erro ao finalizar aprovações:", error);
    }
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        {/* HEADER */}
        <div style={header}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Memorial</h3>
          <button onClick={onClose} style={closeButton}>
            ✕
          </button>
        </div>

        {/* TABELA — área com scroll */}
        <div style={{ flex: 1, overflow: "auto" }}>
          <div className="table-scroll-x">
            <table style={table}>
              <thead>
                <tr>
                  <th style={{ ...th, width: "20%" }}>Foto do falecido</th>
                  <th style={{ ...th, width: "20%" }}>Mensagem</th>
                  <th style={{ ...th, width: "20%" }}>História</th>
                  <th style={{ ...th, width: "12%" }}>Vídeo</th>
                  <th style={{ ...th, width: "14%" }}>Solicitante</th>
                  <th style={{ ...th, width: "14%" }}>Contato</th>
                  <th style={{ ...th, width: "12%", textAlign: "center" }}>
                    Aprovado
                  </th>
                </tr>
              </thead>

              <tbody>
                {currentData.map((item, index) => (
                  <tr key={index}>
                    {/* FOTO + NOME */}
                    <td style={td}>
                      <div style={pessoaContainer}>
                        <img
                          src={Api.getFalecidoFotoUrl(item.image_url)}
                          alt=""
                          style={foto}
                        />
                        <div style={nome}>{item.nomeFalecido}</div>
                      </div>
                    </td>

                    {/* MENSAGEM */}
                    <td style={td}>
                      <div style={scrollText}>{item.mensagem}</div>
                    </td>

                    {/* HISTÓRIA */}
                    <td style={td}>
                      <div style={scrollText}>{item.historia}</div>
                    </td>

                    {/* VÍDEO */}
                    <td style={td}>
                      {item.video ? (
                        <video controls preload="metadata" style={videoStyle}>
                          <source
                            src={Api.getFalecidoVideoUrl(item.video)}
                            type="video/mp4"
                          />
                          Seu navegador não suporta vídeo.
                        </video>
                      ) : (
                        "-"
                      )}
                    </td>

                    {/* SOLICITANTE */}
                    <td style={td}>{item.parentesco}</td>

                    {/* CONTATO */}
                    <td style={td}>{item.telefone}</td>

                    {/* TOGGLE */}
                    <td style={td}>
                      <div style={statusContainer}>
                        <button
                          style={{
                            ...statusButton,
                            ...(item.status === "aprovado"
                              ? approvedStyle
                              : inactiveStyle),
                          }}
                          onClick={() => updateStatus(index, "aprovado")}
                        >
                          ✓
                        </button>
                        <button
                          style={{
                            ...statusButton,
                            ...(item.status === "reprovado"
                              ? rejectedStyle
                              : inactiveStyle),
                          }}
                          onClick={() => updateStatus(index, "reprovado")}
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FOOTER */}
        <div style={footer}>
          <div>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ◀
            </button>

            <span style={{ margin: "0 10px" }}>
              Página {currentPage} de {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              ▶
            </button>
          </div>

          <button onClick={handleFinalizar} style={finalizarBtn}>
            Finalizar
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "20px",
  zIndex: 99999,
};

const modal = {
  width: "min(98vw, 1400px)",
  maxHeight: "calc(100vh - 40px)",
  overflow: "hidden",
  background: "#fff",
  borderRadius: "12px",
  display: "flex",
  flexDirection: "column",
};

const header = {
  background: "linear-gradient(135deg, #F2A66C 0%, #E8956A 100%)",
  padding: "12px 20px",
  color: "#fff",
  display: "flex",
  justifyContent: "space-between",
};

const closeButton = {
  background: "transparent",
  border: "none",
  color: "#fff",
  cursor: "pointer",
  fontSize: "18px",
};

const footer = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "12px 15px",
};

const finalizarBtn = {
  background: "#E8956A",
  border: "none",
  padding: "8px 16px",
  color: "#fff",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: "500",
};

const table = {
  width: "100%",
  borderCollapse: "collapse",
};

const th = {
  padding: "14px 16px",
  textAlign: "left",
  fontSize: "13px",
  fontWeight: "600",
  background: "#f7f4f1",
  borderBottom: "1px solid #ece7e2",
  color: "#666",
  fontFamily: "'DM Sans', sans-serif",
};

const td = {
  padding: "14px 16px",
  borderBottom: "1px solid #f1ece7",
  verticalAlign: "middle",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "14px",
  color: "#444",
};

const pessoaContainer = {
  display: "flex",
  alignItems: "center",
  gap: 14,
};

const foto = {
  width: "100px",
  height: "100px",
  borderRadius: "50%",
  objectFit: "cover",
  flexShrink: 0,
  border: "2px solid #f3e4d7",
};

const nome = {
  fontSize: "14px",
  fontWeight: "600",
  color: "#333",
  lineHeight: "1.3",
};

const statusContainer = {
  display: "flex",
  justifyContent: "center",
  gap: 6,
};

const statusButton = {
  width: 32,
  height: 32,
  borderRadius: "50%",
  border: "none",
  cursor: "pointer",
  fontSize: 14,
  fontWeight: "bold",
  transition: "0.2s",
};

const inactiveStyle = {
  background: "#ececec",
  color: "#777",
};

const approvedStyle = {
  background: "#D8F5DD",
  color: "#2F855A",
};

const rejectedStyle = {
  background: "#FED7D7",
  color: "#C53030",
};

const videoStyle = {
  width: "180px",
  maxWidth: "100%",
  borderRadius: "8px",
};

const scrollText = {
  maxHeight: "120px",
  overflowY: "auto",
  paddingRight: "6px",
  lineHeight: "1.5",
  color: "#555",
  wordBreak: "break-word",
  scrollbarWidth: "thin",
};
